import { FC, useState } from 'react';
import { TemplateType } from '@/api';
import { GlobalAdminDashboardTemplate } from '@/ui/templates';
import {
  CreateOrgSection,
  DataRetentionSection,
  ExpiredCasesSection,
  SwitchOrgSection,
  TemplatesListSection,
  UploadTemplateSection,
} from './components';

/**
 * Global Admin Dashboard — the self-serve control panel for global admins.
 * Four stacked sections: switch organisation, create a new organisation,
 * upload a new template, and browse / delete existing templates.
 *
 * The upload and list sections share the selected `templateType` so a fresh
 * upload lands the user on the matching list, and a `templateListRefreshKey`
 * bump forces the list to refetch after an upload.
 */
export const GlobalAdminDashboardPage: FC = () => {
  const [templateType, setTemplateType] = useState<TemplateType>(TemplateType.CHRONOLOGY);
  const [templateListRefreshKey, setTemplateListRefreshKey] = useState(0);
  // Bumped after a successful org creation so the switcher refetches its list
  // and the new org shows up without a manual page refresh.
  const [orgsRefreshKey, setOrgsRefreshKey] = useState(0);

  const handleUploaded = (uploadedType: TemplateType) => {
    setTemplateType(uploadedType);
    setTemplateListRefreshKey((key) => key + 1);
  };

  const handleOrgCreated = () => {
    setOrgsRefreshKey((key) => key + 1);
  };

  return (
    <GlobalAdminDashboardTemplate>
      <div className="flex w-full flex-col gap-6 px-8 py-8">
        <div className="flex flex-col gap-1">
          <h1 className="text-fletchers-black-900 text-[24px]/[32px] font-semibold">Global Admin Dashboard</h1>
          <p className="text-fletchers-gray-200 text-sm">Manage organisations and custom document templates.</p>
        </div>

        {/* Top row: left column stacks the org switcher + data-retention
            editor (both scoped to the current org); right column is the wider
            create-org form. `items-start` so each column sizes to its own
            content. Collapses to a single column below `lg`. */}
        <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-[2fr_3fr]">
          <div className="flex flex-col gap-6">
            <SwitchOrgSection refreshKey={orgsRefreshKey} />
            <DataRetentionSection />
          </div>
          <CreateOrgSection className="h-full" onCreated={handleOrgCreated} />
        </div>

        {/* Sits under the retention editor it relates to — loads its own list
            on mount and refetches from its Refresh button. */}
        <ExpiredCasesSection />

        {/* Upload + existing templates each take the full width below. */}
        <UploadTemplateSection onUploaded={handleUploaded} />
        <TemplatesListSection
          templateType={templateType}
          onChangeTemplateType={setTemplateType}
          refreshKey={templateListRefreshKey}
        />
      </div>
    </GlobalAdminDashboardTemplate>
  );
};
